import type { FC, FormEvent, ReactNode } from 'react'
import type { GetServerSideProps } from 'next'

import Link from 'next/link'
import { useRouter } from 'next/router'
import { useState } from 'react'
import { getSession } from '@/lib/auth'
import { Seo } from '@/components/generals/seo'
import { AuthShell, safeNext } from '@/components/generals/auth-shell'
import { Alert } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'
import { Field, Input } from '@/components/ui/input'

const Login: FC = (): ReactNode => {
    const router = useRouter()
    const [email, setEmail] = useState('')
    const [password, setPassword] = useState('')
    const [loading, setLoading] = useState(false)
    const [error, setError] = useState<string | null>(null)

    const onSubmit = async (event: FormEvent<HTMLFormElement>) => {
        event.preventDefault()
        setLoading(true)
        setError(null)

        try {
            const response = await fetch('/api/auth/login', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ email, password })
            })
            const data = await response.json()

            if (!response.ok) {
                setError(data.error ?? 'We could not log you in. Please try again.')
                setLoading(false)
                return
            }

            await router.push(safeNext(router.query.next))
        }
        catch {
            setError('We could not reach the server. Check your connection and try again.')
            setLoading(false)
        }
    }

    return (
        <>
            <Seo title='Log in' description='Log in to your Turnout organizer dashboard.' noIndex />

            <AuthShell
                title='Welcome back'
                description='Log in to manage your events, orders and check-in.'
                footer={<>New to Turnout? <Link href='/signup' className='font-semibold text-primary hover:underline'>Create a free account</Link></>}
            >
                <form onSubmit={onSubmit} className='flex flex-col gap-5'>
                    <Field label='Email' htmlFor='login-email'>
                        <Input id='login-email' type='email' autoComplete='email' required value={email} onChange={event => setEmail(event.target.value)} />
                    </Field>

                    <div className='flex flex-col gap-1.5'>
                        <div className='flex items-center justify-between'>
                            <label htmlFor='login-password' className='text-sm font-medium'>Password</label>
                            <Link href='/forgot-password' className='text-sm font-medium text-primary hover:underline'>
                                Forgot password?
                            </Link>
                        </div>
                        <Input id='login-password' type='password' autoComplete='current-password' required value={password} onChange={event => setPassword(event.target.value)} />
                    </div>

                    {error && <Alert variant='error'>{error}</Alert>}

                    <Button type='submit' size='lg' loading={loading}>
                        Log in
                    </Button>
                </form>
            </AuthShell>
        </>
    )
}

export const getServerSideProps: GetServerSideProps = async ({ req }) => {
    const session = await getSession(req)
    if (session) return { redirect: { destination: '/dashboard', permanent: false } }
    return { props: {} }
}

export default Login
